import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useSearchProductBrandsMutation } from '@app/api/Products';
import type { ModelProductBrandsSearchResult } from '@app/api/Products';
import { EDIT_BRAND_PATH } from '@app/constants';
import { useNewUserAccessRights, useUserPrint } from '@app/hooks';
import {
  getBrandsColumns,
  initialFilters,
} from '@app/pages/Warehouses/Brands/data';
import type {
  BrandsFilters,
  UnsavedBrandsFilters,
} from '@app/pages/Warehouses/Brands/types';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

import type { PaginationProps } from 'antd';
import type { ChangeEventHandler } from 'react';

export const useBrands = () => {
  const navigate = useNavigate();
  const { activeBranchId: ActiveBranchId } = useUserPrint();
  const { showNotification } = useNotification();
  const [filters, setFilters] = useState<BrandsFilters>(() => initialFilters);

  const [unsavedFilters, setUnsavedFilters] = useState<UnsavedBrandsFilters>({
    SearchTerm: '',
  });

  const [searchProductBrands, { isLoading: isFetching, isUninitialized }] =
    useSearchProductBrandsMutation();

  const [data, setData] = useState<ModelProductBrandsSearchResult | null>(null);

  const onSearchHandler = useCallback(async () => {
    try {
      const response = await searchProductBrands({
        ActiveBranchId,
        data: filters,
      }).unwrap();

      setData(response);
    } catch (error) {
      showNotification(error, 'error');
    }
  }, [ActiveBranchId, filters, searchProductBrands, showNotification]);

  const {
    userAccessRights: {
      Products: { IsBrands, IsBrandEdit },
    },
  } = useNewUserAccessRights();
  const total = useMemo(() => data?.CountItems ?? 0, [data]);
  const tableData = useMemo(() => data?.Items ?? [], [data]);

  const { ItemsPerPage, Page } = filters;

  const range = useMemo(() => {
    const from = (Page - 1) * ItemsPerPage + 1;
    const to = Math.min(Page * ItemsPerPage, total);
    return { from, to };
  }, [Page, ItemsPerPage, total]);

  const onChangeSearchTerm: ChangeEventHandler<HTMLInputElement> = useCallback(
    ({ target: { value: SearchTerm } }) => {
      setUnsavedFilters((prev) => ({ ...prev, SearchTerm }));
    },
    []
  );

  const skipSearchTerm = useCallback(() => {
    setUnsavedFilters((prev) => ({ ...prev, SearchTerm: '' }));
    setFilters((prevFilters) => ({ ...prevFilters, Page: 1, SearchTerm: '' }));
  }, []);

  const onSearch = useCallback(() => {
    const { SearchTerm } = unsavedFilters;
    setFilters((prevFilters) => ({ ...prevFilters, Page: 1, SearchTerm }));
  }, [unsavedFilters]);

  const onPaginate: PaginationProps['onChange'] = useCallback(
    (Page: number) => {
      setFilters((prevFilters) => ({ ...prevFilters, Page }));
    },
    []
  );

  const onPageSizeChange = useCallback((ItemsPerPage: number) => {
    setFilters((prevFilters) => ({ ...prevFilters, ItemsPerPage, Page: 1 }));
  }, []);

  const onEdit = useCallback(
    (id: number) => {
      navigate(`${EDIT_BRAND_PATH}?id=${id}`);
    },
    [navigate]
  );

  const columns = useMemo(
    () => getBrandsColumns({ onEdit, IsBrandEdit }),
    [onEdit, IsBrandEdit]
  );

  const isPageLoading = useMemo(
    () => isUninitialized || (isFetching && !data),
    [isFetching, isUninitialized, data]
  );

  return {
    filters,
    unsavedFilters,
    onPaginate,
    range,
    onPageSizeChange,
    columns,
    total,
    tableData,
    onChangeSearchTerm,
    skipSearchTerm,
    onSearch,
    onSearchHandler,
    isPageLoading,
    isFetching,
    IsBrands,
  };
};
